import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getMyTenant, TENANT_ME_QUERY_KEY } from '@/lib/api/tenants';
import type { ChannelKind } from '@/lib/api/channel-connections';

import { BigCommerceConnectDialog } from './BigCommerceConnectDialog';
import { ChannelCard } from './ChannelCard';
import { ORDERED_CHANNEL_KINDS } from './channel-catalogue';
import { OnboardingShell } from './OnboardingShell';
import { StepIndicator } from './StepIndicator';

/**
 * Step 3 of 5 — connect a store. Per ADR 0016 this is now a card
 * grid mirroring Step 4 (carriers), with BigCommerce as the only
 * MVP-connectable platform and 11 others rendered as "Coming soon"
 * tiles. The BC tile's Connect button opens an instructions modal
 * since BC always initiates the install (ADR 0011).
 *
 * BC-discovered merchants who came through the BC marketplace skip
 * this step entirely — `firstIncompleteStep` sees their BC
 * connection is already active by the time they reach the wizard.
 * Merchants who came to mailchute.ai first land here, click Connect
 * on the BC tile, follow the modal's instructions inside BC, and
 * loop back automatically via `/bc/oauth/callback`.
 */
export function Step3ConnectStore() {
  const navigate = useNavigate();
  const tenantQuery = useQuery({
    queryKey: TENANT_ME_QUERY_KEY,
    queryFn: async () => {
      const result = await getMyTenant();
      if (result.isErr()) throw new Error(result.error.message);
      return result.value;
    },
  });
  const bcConnection = tenantQuery.data?.bc_connection;
  const isBcConnected = bcConnection?.status === 'active';

  const [bcDialogOpen, setBcDialogOpen] = useState(false);

  const connectedCount = isBcConnected ? 1 : 0;
  const totalCount = ORDERED_CHANNEL_KINDS.length;

  function handleConnect(kind: ChannelKind) {
    if (kind === 'bigcommerce') {
      setBcDialogOpen(true);
    }
  }

  return (
    <OnboardingShell wide>
      <StepIndicator currentStep={3} />
      <Card>
        <CardHeader>
          <CardTitle>Connect your store</CardTitle>
          <CardDescription>
            Pick the platform you sell on. We pull orders from it, generate carrier labels, and push
            tracking back automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-muted-foreground text-xs">
            {connectedCount} of {totalCount} connected · BigCommerce available now, the rest land
            soon.
          </p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="channel-grid">
            {ORDERED_CHANNEL_KINDS.map((kind) => (
              <ChannelCard
                key={kind}
                kind={kind}
                bcConnection={kind === 'bigcommerce' ? bcConnection : undefined}
                onConnect={() => {
                  handleConnect(kind);
                }}
              />
            ))}
          </div>
          <div className="flex justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                void navigate('/app/onboarding/step/2');
              }}
            >
              Back
            </Button>
            <Button
              type="button"
              onClick={() => {
                void navigate('/app/onboarding/step/4');
              }}
            >
              {isBcConnected ? 'Continue' : 'Skip for now'}
            </Button>
          </div>
        </CardContent>
      </Card>
      <BigCommerceConnectDialog
        open={bcDialogOpen}
        onClose={() => {
          setBcDialogOpen(false);
        }}
      />
    </OnboardingShell>
  );
}
